import styled from 'styled-components'

import { Flex, Icon } from 'components'
import Box from 'components/Box'
import Skeleton from 'components/SkeletonLoader'
import Tooltip from 'components/Tooltip'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'

import { LeadActivityType } from 'types/global'
import { colors, HexToRgba, ThemeColor } from 'utils/colors'

type IndicatorProps = {
  color: ThemeColor
  text?: string
}

type Props = {
  icon: JSX.Element
  content: JSX.Element
  additionalContent?: JSX.Element
  isActive: boolean
  isLoading?: boolean
  leadActivityType: LeadActivityType
  warning?: string | boolean
  onClickOpenForm?: () => void
  indicator?: IndicatorProps
  onClickCard?: () => void
  bottomContent?: JSX.Element
  allowContentOverflow?: boolean
}

const ActivityIconBaseColor: { [key in LeadActivityType]: keyof typeof colors } = {
  'Lead Lifecycle Event': 'transparent',
  Appointment: 'blue1',
  Call: 'purple1',
  Email: 'blue1',
  'Email (Log)': 'blue1',
  'Live Call': 'orange1',
  Meeting: 'blue1',
  Note: 'red3',
  'To Do': 'green1',
  Message: 'purple1',
}

const ACTIVITY_TOOLTIP: { [key in LeadActivityType]: string } = {
  'Lead Lifecycle Event': 'Life cycle Event',
  Appointment: 'meeting_lbl',
  Call: 'call_task_lbl',
  Email: 'email_lbl',
  'Email (Log)': 'email_log_lbl',
  'Live Call': 'live_call',
  Meeting: 'meeting_lbl',
  Note: 'note_lbl',
  'To Do': 'todo_task',
  Message: 'call_message_lbl',
}

const ActivityItemWrapper = ({
  icon,
  content,
  additionalContent,
  isActive,
  isLoading = false,
  leadActivityType,
  warning,
  onClickOpenForm,
  indicator,
  onClickCard,
  bottomContent,
  allowContentOverflow = false,
}: Props) => (
  <Box marginBottom="1rem" marginTop="4px" marginRight="0.5rem">
    <Container
      border={`1px solid ${warning ? colors.red : colors.gray3}`}
      backgroundColor={isActive ? 'none' : colors.gray1}
      isActive={isActive}
      cursor="pointer"
      data-testid="test-id-activity-item-wrapper"
      hasWarning={!!warning}
    >
      <Flex onClick={onClickCard} alignItems="flex-start" p={15} gap="15px">
        {isLoading ? (
          <StyledIcon isActive={isActive} isLoading={isLoading} leadActivityType={leadActivityType}>
            <Skeleton />
          </StyledIcon>
        ) : (
          <Tooltip position="bottom" title={ACTIVITY_TOOLTIP[leadActivityType]}>
            <Box>
              <StyledIcon isActive={isActive} isLoading={isLoading} leadActivityType={leadActivityType}>
                {icon}
              </StyledIcon>
            </Box>
          </Tooltip>
        )}
        <Box
          flex="1"
          overflow={allowContentOverflow ? 'visible' : undefined}
          overflowX={allowContentOverflow ? undefined : 'hidden'}
          overflowY={allowContentOverflow ? undefined : 'visible'}
        >
          {content}
        </Box>
        {warning && typeof warning === 'string' && (
          <ClickableBox height="16px" onClick={onClickOpenForm}>
            <Tooltip position="bottomRight" textTransform="none" title={warning}>
              <Icon icon={faExclamationTriangle} color="red" />
            </Tooltip>
          </ClickableBox>
        )}
      </Flex>
      {additionalContent && (
        <Box onClick={onClickCard} padding="10px 16px 16px 16px" borderTop={`1px solid ${colors.gray3}`}>
          {additionalContent}
        </Box>
      )}
      {indicator && <Indicator {...indicator} />}
    </Container>
    {bottomContent}
  </Box>
)

const StyledIcon = styled.div<{ isActive: boolean; isLoading: boolean; leadActivityType: LeadActivityType }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 4px;
  background-color: ${({ theme, isActive, isLoading, leadActivityType }) =>
    isActive
      ? HexToRgba(isLoading ? theme.colors.gray5 : theme.colors[ActivityIconBaseColor[leadActivityType]], 0.16)
      : HexToRgba(theme.colors.gray5, 0.16)};

  svg {
    color: ${({ theme, isActive, leadActivityType }) =>
      isActive ? theme.colors[ActivityIconBaseColor[leadActivityType]] : theme.colors.gray5};
  }
`

const Container = styled(Box)<{ isActive: boolean; hasWarning: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 4px;

  &:hover {
    border-color: ${({ theme, isActive, hasWarning }) => {
      if (isActive && hasWarning) {
        return theme.colors.red
      } else if (isActive && !hasWarning) {
        return theme.colors.blue2
      }
      return theme.colors.gray3
    }};
  }
`

const ClickableBox = styled(Box)`
  cursor: pointer;
`

const Indicator = ({ color, text }: IndicatorProps) => (
  <IndicatorContainer>
    <Tooltip title={text} position="topLeft">
      <Triangle color={color} />
    </Tooltip>
  </IndicatorContainer>
)

const IndicatorContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 1rem;
  height: 1rem;
  border-radius: 0 0 0 4px;
  z-index: 1;
`

const Triangle = styled.div<{ color: ThemeColor }>`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 1rem 1rem 0;
  border-color: transparent transparent ${({ theme, color }) => theme.colors[color]} transparent;
  border-radius: 0 0 0 3px;
`

export { ACTIVITY_TOOLTIP, ActivityItemWrapper }
